import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  onAuthStateChanged,
  signInWithRedirect,
  signOut,
  type User as FirebaseUser,
} from "firebase/auth";

import { buildMicrosoftProvider, FIREBASE_AUTH } from "@/config/firebase";

export interface AuthContextValue {
  readonly firebaseUser: FirebaseUser | null;
  readonly loading: boolean;
  signInMicrosoft(): Promise<void>;
  signOutUser(): Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, (u) => {
      setFirebaseUser(u);
      setLoading(false);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const signInMicrosoft = useCallback(async (): Promise<void> => {
    await signInWithRedirect(FIREBASE_AUTH, buildMicrosoftProvider());
  }, []);

  const signOutUser = useCallback(async (): Promise<void> => {
    await signOut(FIREBASE_AUTH);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ firebaseUser, loading, signInMicrosoft, signOutUser }),
    [firebaseUser, loading, signInMicrosoft, signOutUser],
  );

  // Dev-only Playwright hook — the `import.meta.env.DEV` gate makes Vite
  // dead-code-eliminate this branch in production builds. Used by e2e tests
  // to inject a mock SUPER_ADMIN / USER without hitting real Firebase.
  if (
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    (window as unknown as { __ATE_E2E_AUTH__?: AuthContextValue })
      .__ATE_E2E_AUTH__
  ) {
    const override = (
      window as unknown as { __ATE_E2E_AUTH__: AuthContextValue }
    ).__ATE_E2E_AUTH__;
    return (
      <AuthContext.Provider value={override}>{children}</AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
